import { useMemo } from 'react';
import { CalendarDays, CalendarRange, Grid3x3, PartyPopper, Sparkles, TriangleAlert } from 'lucide-react';
import type { Pagina } from '@miolos/core';
import {
  ROTULO_MODO_FERIADO,
  ROTULO_QUADRO,
  anosDaAgenda,
  feriadosDoAno,
  formatarDataCurta,
  quadrosLateraisDescartados,
  quadrosLigados,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LinhaSwitch, Slider } from '@/components/ui/switch';
import { useEditor } from '../EstadoEditor';

/** Quantas páginas de um tipo entraram no documento atual. */
function contar(paginas: Pagina[], tipo: Pagina['tipo']): number {
  return paginas.filter((pagina) => pagina.tipo === tipo).length;
}

/**
 * Calendários extras.
 *
 * O miolo pode levar os calendários além das páginas de dia: o mini calendário
 * do mês dentro de cada dia, a visão dos 12 meses por ano (inclusive anos
 * anteriores e seguintes) e as divisórias de mês. Tudo mora aqui, no mesmo lugar,
 * para o usuário ver o que marcou e quantas páginas isso acrescenta.
 */
export function CalendariosExtras() {
  const { config, atualizar, alternarExtra, definirCalendario, documento, passos, irParaPasso } = useEditor();
  const { calendario, quadros } = config;

  const temPaginaDeDia = config.paginasDeDia === 'DIA';
  const comMiniNoDia = Boolean(quadros.CALENDARIO_MES);
  const comVisaoDoAno = config.paginasExtras.includes('ANO');
  const comDivisorias = config.paginasExtras.includes('MESES');

  const anos = anosDaAgenda(calendario);
  const feriados = feriadosDoAno(calendario.ano, config.feriados, config.uf || undefined);

  const paginasAno = contar(documento.paginas, 'ANO');
  const paginasMes = contar(documento.paginas, 'MES');
  const paginasDia = contar(documento.paginas, 'DIA');
  /** Só as páginas que os calendários acrescentam (o mini do dia não soma). */
  const totalCalendarios = paginasAno + paginasMes;

  const ligarCalendarioNoDia = (valor: boolean) =>
    atualizar({ quadros: { ...quadros, CALENDARIO_MES: valor } });

  const indiceDoPeriodo = passos.findIndex((passo) => passo.id === 'calendario');
  const indiceDosQuadros = passos.findIndex((passo) => passo.id === 'quadros');

  /**
   * A coluna lateral do dia tem altura limitada. Quando os quadros já ocupam
   * tudo, o mini calendário é omitido — e o usuário precisa saber disso aqui,
   * no momento em que liga o calendário.
   */
  const descartados = useMemo(() => quadrosLateraisDescartados(config), [config]);
  const calendarioSemEspaco = comMiniNoDia && temPaginaDeDia && descartados.includes('CALENDARIO_MES');
  const lateraisNaFrente = quadrosLigados(config.quadros, 'LATERAL').filter(
    (quadro) => quadro !== 'CALENDARIO_MES',
  );
  const ultimoNaFrente = lateraisNaFrente[lateraisNaFrente.length - 1];

  const liberarEspaco = () => {
    if (!ultimoNaFrente) return;
    atualizar({ quadros: { ...quadros, [ultimoNaFrente]: false } });
  };

  return (
    <section className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="space-y-1">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <Sparkles className="size-4 text-primary" />
          Calendários extras
        </h3>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Além das páginas de dia, o miolo pode trazer os calendários: o mês dentro de cada página,
          a visão dos 12 meses de um ano (ou de vários) e as divisórias de mês. Marque o que quiser —
          o total de páginas aparece embaixo.
        </p>
      </div>

      {/* 1. Mini calendário do mês nas páginas de dia */}
      <div className="space-y-3 rounded-md border border-border/70 p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <Grid3x3 className="mt-0.5 size-4 shrink-0 text-primary" />
            <div className="min-w-0">
              <LinhaSwitch
                id="calendario-no-dia"
                rotulo="Mini calendário do mês em cada página de dia"
                descricao="O mês inteiro em grade, com o dia da página destacado e os feriados marcados."
                checked={comMiniNoDia}
                onCheckedChange={ligarCalendarioNoDia}
              />
            </div>
          </div>
          {comMiniNoDia && temPaginaDeDia ? <Badge tom="principal">{paginasDia} página(s)</Badge> : null}
        </div>
        {!temPaginaDeDia ? (
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Este recurso usa a data de cada dia, então só entra com <strong className="text-foreground">1 dia</strong>{' '}
            ou <strong className="text-foreground">2 dias por página</strong>. A visão do ano e as divisórias de mês
            continuam valendo.
          </p>
        ) : null}

        {calendarioSemEspaco ? (
          <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-[11px] leading-relaxed text-amber-700 dark:text-amber-400">
            <p className="flex items-start gap-2">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              <span>
                O mini calendário está marcado, mas <strong>não coube na coluna lateral</strong>: em{' '}
                {config.formato} com {config.diasPorPagina} dia(s) por página, os quadros já usam toda a altura
                {lateraisNaFrente.length > 0
                  ? ` (hoje: ${lateraisNaFrente.map((quadro) => ROTULO_QUADRO[quadro]).join(', ')})`
                  : ''}
                . Desligue um quadro para ele entrar.
              </span>
            </p>
            <div className="flex flex-wrap gap-2">
              {ultimoNaFrente ? (
                <Button variant="contorno" tamanho="pequeno" onClick={liberarEspaco}>
                  Desligar “{ROTULO_QUADRO[ultimoNaFrente]}”
                </Button>
              ) : null}
              {indiceDosQuadros >= 0 ? (
                <Button variant="contorno" tamanho="pequeno" onClick={() => irParaPasso(indiceDosQuadros)}>
                  Ajustar quadros do dia
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {/* 2. Visão do ano, com os anos extras */}
      <div className="space-y-3 rounded-md border border-border/70 p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <CalendarRange className="mt-0.5 size-4 shrink-0 text-primary" />
            <div className="min-w-0">
              <LinhaSwitch
                id="visao-do-ano"
                rotulo="Visão do ano (os 12 meses em miniaturas)"
                descricao="Uma página por ano, com janeiro a dezembro lado a lado — para planejar o que atravessa a virada."
                checked={comVisaoDoAno}
                onCheckedChange={() => alternarExtra('ANO')}
              />
            </div>
          </div>
          {comVisaoDoAno ? (
            <Badge tom="principal">{paginasAno} página(s) · {anos.length} ano(s)</Badge>
          ) : null}
        </div>

        {comVisaoDoAno ? (
          <div className="space-y-3 pl-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Anos para trás</span>
                  <span className="font-mono text-xs text-muted-foreground">{calendario.anosParaTras}</span>
                </div>
                <Slider
                  value={[calendario.anosParaTras]}
                  min={0}
                  max={5}
                  step={1}
                  onValueChange={([valor]) => definirCalendario({ anosParaTras: valor ?? 0 })}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Anos para frente</span>
                  <span className="font-mono text-xs text-muted-foreground">{calendario.anosParaFrente}</span>
                </div>
                <Slider
                  value={[calendario.anosParaFrente]}
                  min={0}
                  max={5}
                  step={1}
                  onValueChange={([valor]) => definirCalendario({ anosParaFrente: valor ?? 0 })}
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Anos incluídos:</span>
              {anos.map((ano) => (
                <Badge key={ano} tom={ano === calendario.ano ? 'principal' : 'neutro'}>
                  {ano}
                  {ano === calendario.ano ? ' · referência' : ''}
                </Badge>
              ))}
            </div>

            <p className="text-[11px] leading-relaxed text-muted-foreground">
              {anos.length === 1
                ? 'Somente o ano de referência — nenhuma página extra de calendário.'
                : `${anos.length} anos na agenda: ${paginasAno} página(s) de visão do ano em A4 e A5 (2 por ano em A6).`}
            </p>
          </div>
        ) : null}
      </div>

      {/* 3. Divisórias de mês */}
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border/70 p-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <CalendarDays className="mt-0.5 size-4 shrink-0 text-primary" />
          <div className="min-w-0">
            <LinhaSwitch
              id="divisorias-de-mes"
              rotulo="Divisórias de mês com mini calendário"
              descricao="Cada mês abre com uma página de respiro, o calendário do mês e espaço para as datas importantes."
              checked={comDivisorias}
              onCheckedChange={() => alternarExtra('MESES')}
            />
          </div>
        </div>
        {comDivisorias ? <Badge tom="principal">{paginasMes} página(s)</Badge> : null}
      </div>

      {/* 4. Feriados: o que entra nos calendários */}
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-border/70 p-3">
        <PartyPopper className="size-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <span className="block text-sm font-medium">
            Feriados nos calendários: {ROTULO_MODO_FERIADO[config.feriados].toLowerCase()}
            {config.feriados === 'ESTADUAIS' && config.uf ? ` (${config.uf})` : ''}
          </span>
          <span className="block text-xs leading-relaxed text-muted-foreground">
            {config.feriados === 'NENHUM'
              ? 'Nenhum feriado está marcado — nem nos mini calendários, nem nas páginas de dia.'
              : `${feriados.length} feriados em ${calendario.ano}${
                  feriados[0] ? `, começando por ${formatarDataCurta(feriados[0].iso)} (${feriados[0].nome})` : ''
                }. Eles aparecem com ponto colorido nos mini calendários e com o nome no cabeçalho das páginas de dia.`}
          </span>
        </div>
        {indiceDoPeriodo >= 0 ? (
          <Button variant="contorno" tamanho="pequeno" onClick={() => irParaPasso(indiceDoPeriodo)}>
            Escolher os feriados
          </Button>
        ) : null}
      </div>

      <p className="rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
        Páginas extras de calendário:{' '}
        <strong className="text-foreground">
          {totalCalendarios === 0
            ? 'nenhuma'
            : `${totalCalendarios} (${paginasAno} de visão do ano + ${paginasMes} de divisórias de mês)`}
        </strong>
        .{' '}
        {comMiniNoDia && temPaginaDeDia
          ? `O mini calendário do mês entra dentro das ${paginasDia} páginas de dia, sem acrescentar páginas — ele usa o espaço da coluna lateral; se o dia estiver cheio de quadros, desligue algum para ele caber.`
          : 'O mini calendário do mês, quando ligado, entra dentro das páginas de dia, sem acrescentar páginas.'}
      </p>
    </section>
  );
}
